// StartCloseRatio component - adapted from CloseRatio for start/ path
import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, Target, BarChart, CheckCircle2, XCircle, Clock, ChevronDown, Search, Edit2, Trash2, Save, X, User, ChevronRight, Brain, Percent } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import { getGoalsForMonth } from '../services/goalsService';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaArrowRight } from 'react-icons/fa';
import StartSidebar from '../components/StartSidebar';

const StartCloseRatio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { isOnboardingMode, markStepComplete } = useOnboarding();
  const [selectedTimeRange, setSelectedTimeRange] = useState('allTime');
  const [activeTab, setActiveTab] = useState('stats');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [selectedSalesRep, setSelectedSalesRep] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    scope: '',
    date: '',
    status: 'open',
    value: '',
    reason: '',
    salesRep: ''
  });
  const [leads, setLeads] = useState([]);
  const [conversionRateGoal, setConversionRateGoal] = useState(70); // Default fallback
  const [loadingGoal, setLoadingGoal] = useState(true);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  
  // Onboarding state
  const [closeRatio, setCloseRatio] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Sales reps and project types management
  const [salesReps, setSalesReps] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 5;
  
  // Load user data from Firestore
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          
          // Load leads data from user data
          const userLeads = data.salesData?.leads || [];
          setLeads(userLeads);
          
          // Load sales reps and project types from user data
          if (data.salesData?.salesReps) {
            setSalesReps(data.salesData.salesReps);
          }
          if (data.salesData?.projectTypes) {
            setProjectTypes(data.salesData.projectTypes);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);
  
  // Fetch user's conversion rate goal
  useEffect(() => {
    const fetchConversionRateGoal = async () => {
      if (!currentUser) return;
      
      try {
        setLoadingGoal(true);
        const currentDate = new Date();
        const goals = await getGoalsForMonth(currentDate.getFullYear(), currentDate.getMonth() + 1);
        
        // Find the conversion rate goal in the sales category
        const conversionGoal = goals.sales?.find(goal => goal.metricKey === 'conversionRate');
        if (conversionGoal) {
          setConversionRateGoal(conversionGoal.target);
        }
        // If no goal is found, keep the default 70%
      } catch (error) {
        console.error('Error fetching conversion rate goal:', error);
        // Keep default 70% if there's an error
      } finally {
        setLoadingGoal(false);
      }
    };

    fetchConversionRateGoal();
  }, [currentUser]);
  
  // Load existing close ratio data in onboarding mode
  useEffect(() => {
    const loadCloseRatioData = async () => {
      if (currentUser && isOnboardingMode) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.salesData?.closeRatio !== undefined) {
              setCloseRatio(userData.salesData.closeRatio.toString());
            }
          }
        } catch (error) {
          console.error('Error loading close ratio data:', error);
        }
      }
    };

    loadCloseRatioData();
  }, [currentUser, isOnboardingMode]);
  
  // Handle close ratio submission for onboarding
  const handleCloseRatioSubmit = async () => {
    if (isOnboardingMode && currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          'salesData.closeRatio': parseFloat(closeRatio) || 0,
          lastUpdated: new Date().toISOString()
        });
        
        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
          // Navigate back to sales page
          navigate('/start/sales');
        }, 1500);
      } catch (error) {
        console.error('Error saving close ratio:', error);
      }
    }
  };
  
  // Save leads data to Firestore
  const saveLeadsToFirestore = async (updatedLeads) => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'salesData.leads': updatedLeads,
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving leads data:', error);
    }
  };
  
  // Generate historical data based on selected time range
  const getHistoricalData = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (selectedTimeRange) {
      case 'thisMonth':
        // Show daily data for current month
        const daysInMonth = new Date(currentYear, now.getMonth() + 1, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const dayLeads = leads.filter(lead => {
            const leadDate = new Date(lead.date);
            return leadDate.getDate() === day && 
                   leadDate.getMonth() === now.getMonth() && 
                   leadDate.getFullYear() === currentYear;
          });
          const total = dayLeads.length;
          const closed = dayLeads.filter(lead => lead.status === 'closed').length;
          return {
            day: day.toString(),
            closeRatio: total > 0 ? (closed / total) * 100 : 0
          };
        });
      case 'thisQuarter':
        // Show monthly data for current quarter
        const quarter = Math.floor(now.getMonth() / 3);
        const quarterStartMonth = quarter * 3;
        return Array.from({ length: 3 }, (_, i) => {
          const month = quarterStartMonth + i;
          const monthLeads = leads.filter(lead => {
            const leadDate = new Date(lead.date);
            return leadDate.getMonth() === month && leadDate.getFullYear() === currentYear;
          });
          const total = monthLeads.length;
          const closed = monthLeads.filter(lead => lead.status === 'closed').length;
          return {
            month: new Date(currentYear, month).toLocaleDateString('en-US', { month: 'short' }),
            closeRatio: total > 0 ? (closed / total) * 100 : 0
          };
        });
      case 'thisYear':
        // Show monthly data for current year
        return Array.from({ length: 12 }, (_, i) => {
          const monthLeads = leads.filter(lead => {
            const leadDate = new Date(lead.date);
            return leadDate.getMonth() === i && leadDate.getFullYear() === currentYear;
          });
          const total = monthLeads.length;
          const closed = monthLeads.filter(lead => lead.status === 'closed').length;
          return {
            month: new Date(currentYear, i).toLocaleDateString('en-US', { month: 'short' }),
            closeRatio: total > 0 ? (closed / total) * 100 : 0
          };
        });
      case 'lastYear':
        // Show monthly data for last year
        return Array.from({ length: 12 }, (_, i) => {
          const monthLeads = leads.filter(lead => {
            const leadDate = new Date(lead.date);
            return leadDate.getMonth() === i && leadDate.getFullYear() === currentYear - 1;
          });
          const total = monthLeads.length;
          const closed = monthLeads.filter(lead => lead.status === 'closed').length;
          return {
            month: new Date(currentYear - 1, i).toLocaleDateString('en-US', { month: 'short' }),
            closeRatio: total > 0 ? (closed / total) * 100 : 0
          };
        });
      case 'allTime':
      default:
        // Show monthly data for all time (last 12 months)
        return Array.from({ length: 12 }, (_, i) => {
          const targetDate = new Date(now);
          targetDate.setMonth(now.getMonth() - (11 - i));
          const monthLeads = leads.filter(lead => {
            const leadDate = new Date(lead.date);
            return leadDate.getMonth() === targetDate.getMonth() && 
                   leadDate.getFullYear() === targetDate.getFullYear();
          });
          const total = monthLeads.length;
          const closed = monthLeads.filter(lead => lead.status === 'closed').length;
          return {
            month: targetDate.toLocaleDateString('en-US', { month: 'short' }),
            closeRatio: total > 0 ? (closed / total) * 100 : 0
          };
        });
    }
  };
  
  const historicalData = getHistoricalData();
  
  // Filter leads based on search query, status filter, scope filter, and sales rep
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchQuery === '' || 
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.scope.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === '' || lead.status === statusFilter;
    const matchesScope = scopeFilter === '' || lead.scope === scopeFilter;
    const matchesSalesRep = selectedSalesRep === '' || lead.salesRep === selectedSalesRep;
    
    return matchesSearch && matchesStatus && matchesScope && matchesSalesRep;
  });
  
  // Calculate metrics based on filtered leads and time range
  const calculateMetrics = () => {
    // Filter leads by time range
    const now = new Date();
    const filteredByTime = filteredLeads.filter(lead => {
      const leadDate = new Date(lead.date);
      
      switch (selectedTimeRange) {
        case 'thisMonth':
          return leadDate.getMonth() === now.getMonth() && 
                 leadDate.getFullYear() === now.getFullYear();
        case 'thisQuarter':
          const quarter = Math.floor(now.getMonth() / 3);
          const leadQuarter = Math.floor(leadDate.getMonth() / 3);
          return leadQuarter === quarter && leadDate.getFullYear() === now.getFullYear();
        case 'thisYear':
          return leadDate.getFullYear() === now.getFullYear();
        case 'lastYear':
          return leadDate.getFullYear() === now.getFullYear() - 1;
        case 'allTime':
        default:
          return true;
      }
    });
    
    const total = filteredByTime.length;
    const closed = filteredByTime.filter(lead => lead.status === 'closed').length;
    const lost = filteredByTime.filter(lead => lead.status === 'lost').length;
    const open = filteredByTime.filter(lead => lead.status === 'open').length;
    const closeRatio = total > 0 ? (closed / total) * 100 : 0;
    
    return {
      total,
      closed,
      lost,
      open,
      closeRatio
    };
  };
  
  const metrics = calculateMetrics();
  
  // Pagination logic
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  
  const timeRanges = [
    { value: 'allTime', label: 'All Time' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'lastYear', label: 'Last Year' }
  ];
  
  const handleAddNew = () => {
    setFormData({
      name: '',
      scope: '',
      date: '',
      status: 'open',
      value: '',
      reason: '',
      salesRep: ''
    });
    setEditingLead(null);
    setShowModal(true);
  };
  
  const handleEdit = (lead) => {
    setFormData(lead);
    setEditingLead(lead);
    setShowModal(true);
  };
  
  const handleDelete = (lead) => {
    setLeadToDelete(lead);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    if (leadToDelete) {
      const updatedLeads = leads.filter(lead => lead.id !== leadToDelete.id);
      setLeads(updatedLeads);
      await saveLeadsToFirestore(updatedLeads);
      setShowDeleteModal(false);
      setLeadToDelete(null);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };
  
  const handleSave = async () => {
    if (editingLead) {
      // Update existing lead
      const updatedLeads = leads.map(lead => 
        lead.id === editingLead.id ? { ...formData, id: lead.id } : lead
      );
      setLeads(updatedLeads);
      await saveLeadsToFirestore(updatedLeads);
    } else {
      // Add new lead
      const newLead = {
        ...formData,
        id: Date.now() // Use timestamp for unique ID
      };
      const updatedLeads = [...leads, newLead];
      setLeads(updatedLeads);
      await saveLeadsToFirestore(updatedLeads);
    }
    setShowModal(false);
    setEditingLead(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Add new sales rep
  const addSalesRep = async () => {
    if (newRepName.trim()) {
      const newRep = {
        id: `rep-${Date.now()}`,
        name: newRepName.trim()
      };
      const updatedReps = [...salesReps, newRep];
      setSalesReps(updatedReps);
      setNewRepName('');
      
      // Save to Firestore
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            'salesData.salesReps': updatedReps,
            lastUpdated: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving sales reps:', error);
        }
      }
    }
  };
  
  // Add new project type
  const addProjectType = async () => {
    if (newProjectTypeName.trim()) {
      const newType = {
        id: `type-${Date.now()}`,
        name: newProjectTypeName.trim()
      };
      const updatedTypes = [...projectTypes, newType];
      setProjectTypes(updatedTypes);
      setNewProjectTypeName('');
      
      // Save to Firestore
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            'salesData.projectTypes': updatedTypes,
            lastUpdated: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving project types:', error);
        }
      }
    }
  };
  
  // Edit sales rep
  const editSalesRep = async () => {
    if (editingRep && newRepName.trim()) {
      const updatedReps = salesReps.map(rep => 
        rep.id === editingRep.id ? { ...rep, name: newRepName.trim() } : rep
      );
      setSalesReps(updatedReps);
      setEditingRep(null);
      setNewRepName('');
      
      // Save to Firestore
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            'salesData.salesReps': updatedReps,
            lastUpdated: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving sales reps:', error);
        }
      }
    }
  };
  
  // Edit project type
  const editProjectType = async () => {
    if (editingProjectType && newProjectTypeName.trim()) {
      const updatedTypes = projectTypes.map(type => 
        type.id === editingProjectType.id ? { ...type, name: newProjectTypeName.trim() } : type
      );
      setProjectTypes(updatedTypes);
      setEditingProjectType(null);
      setNewProjectTypeName('');
      
      // Save to Firestore
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            'salesData.projectTypes': updatedTypes,
            lastUpdated: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving project types:', error);
        }
      }
    }
  };
  
  // Delete sales rep
  const deleteSalesRep = async (repId) => {
    const updatedReps = salesReps.filter(rep => rep.id !== repId);
    setSalesReps(updatedReps);
    
    // Save to Firestore
    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          'salesData.salesReps': updatedReps,
          lastUpdated: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving sales reps:', error);
      }
    }
  };
  
  // Delete project type
  const deleteProjectType = async (typeId) => {
    const updatedTypes = projectTypes.filter(type => type.id !== typeId);
    setProjectTypes(updatedTypes);
    
    // Save to Firestore
    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          'salesData.projectTypes': updatedTypes,
          lastUpdated: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving project types:', error);
      }
    }
  };
  
  // Start editing rep
  const startEditRep = (rep) => {
    setEditingRep(rep);
    setNewRepName(rep.name);
  };
  
  // Start editing project type
  const startEditProjectType = (type) => {
    setEditingProjectType(type);
    setNewProjectTypeName(type.name);
  };
  
  // Cancel editing
  const cancelEdit = () => {
    setEditingRep(null);
    setEditingProjectType(null);
    setNewRepName('');
    setNewProjectTypeName('');
  };
  
  const [showLostList, setShowLostList] = useState(false);
  const [lostReasons, setLostReasons] = useState({});
  
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  
  // Management popup state
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [editingProjectType, setEditingProjectType] = useState(null);
  const [newRepName, setNewRepName] = useState('');
  const [newProjectTypeName, setNewProjectTypeName] = useState('');
  
  // Calculate lost reasons whenever leads change
  useEffect(() => {
    const reasons = {};
    leads.forEach(lead => {
      if (lead.status === 'lost' && lead.reason) {
        reasons[lead.reason] = (reasons[lead.reason] || 0) + 1;
      }
    });
    setLostReasons(reasons);
  }, [leads]);
  
  // Recalculate metrics when time range changes
  useEffect(() => {
    // This will trigger a re-render with updated metrics
  }, [selectedTimeRange]);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, scopeFilter, selectedSalesRep]);
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen">
        <StartSidebar />
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center min-h-[400px]">
              <BBALoader size={128} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className="p-6">
        {/* Back Button and Title Row */}
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/start/sales')} />
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Close Ratio Analytics</h1>
          {!isOnboardingMode && <AddButton onClick={handleAddNew} />}
        </div>
        
        {/* Success Message for Onboarding */}
        {isOnboardingMode && showSuccessMessage && (
          <div className="bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded relative mb-4" role="alert">
            <strong className="font-bold">Success!</strong>
            <span className="block sm:inline"> Close ratio saved! Redirecting back to sales...</span>
          </div>
        )}
        
        {/* Combined Close Ratio Card with Tabs */}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-md dark:shadow-gray-800 mb-6">
          {/* Tabs - Hidden in onboarding mode */}
          {!isOnboardingMode && (
            <div className="flex justify-center space-x-8 mb-4">
              <button
                onClick={() => setActiveTab('stats')}
                className={`${
                  activeTab === 'stats'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                } p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200`}
                title="Stats View"
              >
                <BarChart className="w-6 h-6" />
              </button>
              <button
                onClick={() => setActiveTab('graph')}
                className={`${
                  activeTab === 'graph'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                } p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200`}
                title="Graph View"
              >
                <TrendingUp className="w-6 h-6" />
              </button>
            </div>
          )}
          
          {activeTab === 'stats' ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Close Ratio Performance</h2>
                </div>
                {!isOnboardingMode && (
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <select
                        value={selectedSalesRep}
                        onChange={(e) => setSelectedSalesRep(e.target.value)}
                        className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                      >
                        <option value="">All Reps</option>
                        {salesReps.map((rep) => (
                          <option key={rep.id} value={rep.id}>
                            {rep.name}
                          </option>
                        ))}
                      </select>
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                      <button
                        onClick={() => setShowManagementModal(true)}
                        className="absolute -right-8 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Manage sales reps and project types"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="relative">
                      <select
                        value={selectedTimeRange}
                        onChange={(e) => setSelectedTimeRange(e.target.value)}
                        className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                      >
                        {timeRanges.map((range) => (
                          <option key={range.value} value={range.value}>
                            {range.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="text-center mb-4">
                {isOnboardingMode ? (
                  <div>
                    <div className="relative inline-block">
                      <input
                        type="number"
                        value={closeRatio}
                        onChange={(e) => setCloseRatio(e.target.value)}
                        className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 bg-transparent border-b-2 border-emerald-600 dark:border-emerald-400 text-center w-24 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-300"
                        placeholder="0"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">%</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Enter your current close ratio</p>
                    <button
                      onClick={handleCloseRatioSubmit}
                      disabled={!closeRatio || parseFloat(closeRatio) < 0 || parseFloat(closeRatio) > 100}
                      className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save & Continue
                      <FaArrowRight className="ml-2" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{metrics.closeRatio.toFixed(1)}%</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Current Ratio</p>
                  </div>
                )}
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
                <div 
                  className="bg-emerald-600 h-2.5 rounded-full" 
                  style={{ width: `${isOnboardingMode ? (parseFloat(closeRatio) || 0) : metrics.closeRatio}%` }}
                ></div>
              </div>
              
              {/* Status Count Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="flex items-center mb-2">
                      <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 mr-2" />
                      <span className="text-base font-medium text-gray-600 dark:text-gray-300">Won</span>
                    </div>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">{metrics.closed}</span>
                  </div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="flex items-center mb-2">
                      <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 mr-2" />
                      <span className="text-base font-medium text-gray-600 dark:text-gray-300">Lost</span>
                    </div>
                    <span className="text-2xl font-bold text-red-600 dark:text-red-400">{metrics.lost}</span>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="flex items-center mb-2">
                      <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mr-2" />
                      <span className="text-base font-medium text-gray-600 dark:text-gray-300">Open</span>
                    </div>
                    <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{metrics.open}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <BarChart className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Historical Trends</h2>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <select
                      value={selectedSalesRep}
                      onChange={(e) => setSelectedSalesRep(e.target.value)}
                      className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                    >
                      <option value="">All Reps</option>
                      {salesReps.map((rep) => (
                        <option key={rep.id} value={rep.id}>
                          {rep.name}
                        </option>
                      ))}
                    </select>
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                  </div>
                  <div className="relative">
                    <select
                      value={selectedTimeRange}
                      onChange={(e) => setSelectedTimeRange(e.target.value)}
                      className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                    >
                      {timeRanges.map((range) => (
                        <option key={range.value} value={range.value}>
                          {range.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historicalData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={selectedTimeRange === 'thisMonth' ? 'day' : 'month'} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      formatter={(value) => [`${value.toFixed(1)}%`, 'Close Ratio']}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="closeRatio" 
                      name="Close Ratio" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={{ r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
        
        {/* Prospects Table */}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-md dark:shadow-gray-800 mb-6">
          <div className="text-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Prospects</h2>
          </div>
          
          {/* Search and Filter Section */}
          <div className="flex items-center justify-between mb-6">
            <div className="relative flex-1 mr-4">
              <input
                type="text"
                placeholder="Search prospects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <select
                  value={selectedSalesRep}
                  onChange={(e) => setSelectedSalesRep(e.target.value)}
                  className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                >
                  <option value="">All Reps</option>
                  {salesReps.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.name}
                    </option>
                  ))}
                </select>
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                <button
                  onClick={() => setShowManagementModal(true)}
                  className="absolute -right-8 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  title="Manage sales reps and project types"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <div className="relative">
                <select
                  value={scopeFilter}
                  onChange={(e) => setScopeFilter(e.target.value)}
                  className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                >
                  <option value="">All Scopes</option>
                  {projectTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
                <button
                  onClick={() => setShowManagementModal(true)}
                  className="absolute -right-8 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  title="Manage project types"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:text-white"
                >
                  <option value="">All</option>
                  <option value="closed">Won</option>
                  <option value="lost">Lost</option>
                  <option value="open">Open</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Scope</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Value</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sales Rep</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {currentLeads.length > 0 ? (
                  currentLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{lead.scope}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{lead.date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {lead.value ? `$${parseFloat(lead.value).toLocaleString()}` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {lead.salesRep ? salesReps.find(rep => rep.id === lead.salesRep)?.name : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {lead.status === 'closed' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Won
                          </span>
                        ) : lead.status === 'lost' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300">
                            <XCircle className="w-4 h-4 mr-1" />
                            Lost
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300">
                            <Clock className="w-4 h-4 mr-1" />
                            Open
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleEdit(lead)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(lead)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center">
                      <div className="text-gray-500 dark:text-gray-400">
                        <p className="text-lg font-medium mb-2">No leads found</p>
                        <p className="text-sm">Add your first lead to start tracking your close ratio</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {indexOfFirstLead + 1} to {Math.min(indexOfLastLead, filteredLeads.length)} of {filteredLeads.length} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      currentPage === page
                        ? 'bg-red-600 text-white border-red-600'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Additional Analysis Cards */}
        <div className="grid grid-cols-2 gap-6">
          {/* Lost List Card */}
          <div 
            onClick={() => setShowLostList(true)}
            className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-md dark:shadow-gray-800 cursor-pointer hover:shadow-lg dark:hover:shadow-gray-700 transition-shadow duration-200"
          >
            <div className="text-center">
              <div className="flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Lost List</h2>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-300">View reasons why leads were lost</p>
            </div>
          </div>
          
          {/* AI Insights Card */}
          <div 
            onClick={() => setShowAIInsights(true)}
            className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-md dark:shadow-gray-800 cursor-pointer hover:shadow-lg dark:hover:shadow-gray-700 transition-shadow duration-200"
          >
            <div className="text-center">
              <div className="flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">AI Insights</h2>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-300">Visual analytics of lost leads data</p>
            </div>
          </div>
        </div>
        
        {/* Modals - Same as CloseRatio.js but with navigation to /start/sales */}
        {/* Lost List Modal */}
        {showLostList && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-2xl w-full relative">
              <button
                onClick={() => setShowLostList(false)}
                className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-4">
                <div className="flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Lost Lead Analysis</h3>
                </div>
              </div>
              <div className="space-y-4">
                {Object.keys(lostReasons).length > 0 ? (
                  <>
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                          ${leads.filter(lead => lead.status === 'lost').reduce((sum, lead) => sum + (parseFloat(lead.value) || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-red-600 dark:text-red-400">Total Value Lost</div>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                          {leads.filter(lead => lead.status === 'lost').length}
                        </div>
                        <div className="text-sm text-orange-600 dark:text-orange-400">Total Leads Lost</div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {new Set(leads.filter(lead => lead.status === 'lost').map(lead => lead.scope)).size}
                        </div>
                        <div className="text-sm text-blue-600 dark:text-blue-400">Scopes Affected</div>
                      </div>
                    </div>
                    
                    {/* Detailed Breakdown */}
                    <div className="space-y-3">
                      {Object.entries(lostReasons).map(([reason, count]) => {
                        const lostLeads = leads.filter(lead => lead.status === 'lost' && lead.reason === reason);
                        const totalValue = lostLeads.reduce((sum, lead) => sum + (parseFloat(lead.value) || 0), 0);
                        const scopes = [...new Set(lostLeads.map(lead => lead.scope))];
                        const reps = [...new Set(lostLeads.map(lead => lead.salesRep))];
                        
                        return (
                          <div key={reason} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-700 dark:text-gray-300">{reason}</span>
                              <span className="text-red-600 dark:text-red-400 font-semibold">{count} leads</span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Value Lost: </span>
                                <span className="font-medium text-red-600 dark:text-red-400">${totalValue.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Scopes: </span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">{scopes.join(', ') || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Reps: </span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {reps.map(repId => salesReps.find(rep => rep.id === repId)?.name || 'Unknown').join(', ')}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No lost leads data available yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* AI Insights Modal - Simplified version */}
        {showAIInsights && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={() => setShowAIInsights(false)}
                className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-6">
                <div className="flex items-center justify-center">
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Lost Leads Analytics</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mt-2">Comprehensive analysis of your lost leads data</p>
              </div>
              {/* Add AI Insights content here - same as CloseRatio.js */}
            </div>
          </div>
        )}
        
        {/* Existing Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {editingLead ? 'Edit Lead' : 'Add New Lead'}
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Scope</label>
                  <div className="flex space-x-2">
                    <select
                      name="scope"
                      value={formData.scope}
                      onChange={handleInputChange}
                      className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">Select Project Type</option>
                      {projectTypes.map((type) => (
                        <option key={type.id} value={type.name}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowManagementModal(true)}
                      className="px-3 py-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md"
                      title="Manage project types"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Value (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                    <input
                      type="text"
                      name="value"
                      value={formData.value}
                      onChange={handleInputChange}
                      className="pl-8 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sales Representative</label>
                  <div className="flex space-x-2">
                    <select
                      name="salesRep"
                      value={formData.salesRep}
                      onChange={handleInputChange}
                      className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">Select Representative</option>
                      {salesReps.map((rep) => (
                        <option key={rep.id} value={rep.id}>
                          {rep.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowManagementModal(true)}
                      className="px-3 py-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md"
                      title="Manage sales reps"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="open">Open</option>
                    <option value="closed">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                {formData.status === 'lost' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reason</label>
                    <input
                      type="text"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-800 dark:text-white"
                      placeholder="Why was this lead lost?"
                    />
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <Save className="w-4 h-4 inline-block mr-2" />
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                  Delete Lead
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Are you sure you want to delete <span className="font-semibold text-gray-800 dark:text-white">{leadToDelete?.name}</span>?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Management Modal */}
        {showManagementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Manage Sales Reps & Project Types</h3>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Sales Reps Management */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Sales Representatives</h4>
                  
                  {/* Add/Edit Sales Rep */}
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex space-x-2 mb-3">
                      <input
                        type="text"
                        value={newRepName}
                        onChange={(e) => setNewRepName(e.target.value)}
                        placeholder="Enter sales rep name"
                        className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                      {editingRep ? (
                        <>
                          <button
                            onClick={editSalesRep}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Update
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={addSalesRep}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Sales Reps List */}
                  <div className="space-y-2">
                    {salesReps.map((rep) => (
                      <div key={rep.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">{rep.name}</span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditRep(rep)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteSalesRep(rep.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Project Types Management */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Project Types</h4>
                  
                  {/* Add/Edit Project Type */}
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex space-x-2 mb-3">
                      <input
                        type="text"
                        value={newProjectTypeName}
                        onChange={(e) => setNewProjectTypeName(e.target.value)}
                        placeholder="Enter project type name"
                        className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                      {editingProjectType ? (
                        <>
                          <button
                            onClick={editProjectType}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Update
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={addProjectType}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Project Types List */}
                  <div className="space-y-2">
                    {projectTypes.map((type) => (
                      <div key={type.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <span className="text-gray-700 dark:text-gray-300">{type.name}</span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditProjectType(type)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteProjectType(type.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Save and Close Button */}
              <div className="flex justify-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowManagementModal(false)}
                  className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Save & Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartCloseRatio;

